import { useQueryClient } from '@tanstack/react-query';
import { FormikHelpers } from 'formik';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../contexts/AuthProvider';
import { useMutationModalContext } from '../../contexts/MutationModalProvider';
import useAssociateActions from '../../hooks/APIHooks/users/useAssociateActions';
import useAssociateRequestActions from '../../hooks/APIHooks/users/useAssociateRequestActions';
import useUserProfileActions from '../../hooks/APIHooks/users/useUserProfileActions';
import { Associate, AssociatePasswordChangeBody } from '../../types/associates';
import { showFullName } from '../../utils/commonFunctions';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import ApplicationConsentModal from '../CommonComps/Modals/ApplicationConsentModal';
import ChangePasswordModal from '../CommonComps/Modals/ChangePasswordModal';
import ConsentModal from '../CommonComps/Modals/ConsentModal';
import CreateEditAssociateModal from '../CommonComps/Modals/CreateEditAssociateModal';
import UpdatePermissionsModal from '../CommonComps/Modals/UpdatePermissionsModal';

const AssociateMutationModals = () => {
  // integration of react hooks here
  const [isDeactivating, setIsDeactivating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    data,
    showUpdateModal,
    setShowUpdateModal,
    showUpdatePermissionModal,
    setShowUpdatePermissionModal,
    showPasswordModal,
    setShowPasswordModal,
    showDeactivateModal,
    setShowDeactivateModal,
    showDeleteModal,
    setShowDeleteModal,
    showRejectModal,
    setShowRejectModal,
  } = useMutationModalContext();

  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const {
    changeAssociatePasswordHandler,
    deleteAssociateHandler,
    toggleAssociateActivityHandler,
  } = useAssociateActions('');

  const { updateAssociateStatusHandler } = useAssociateRequestActions('');
  const { getUserProfileByIdHandler } = useUserProfileActions(
    '',
    showUpdateModal ? (data as Associate).user_profile_id : undefined
  );

  console.log('USER PROFILE DATA: ', getUserProfileByIdHandler.data?.data);

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to delete associate declared here
  const deleteHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteAssociateHandler
        .mutateAsync({
          associateId: (data as Associate).id,
        })
        .then((res) => {
          console.log('DELETE ASSOCIATE RES: ', res);

          if (res.status === 204) {
            toast.success('Associate Deleted!!');

            setShowDeleteModal(false);
            queryClient.invalidateQueries({ queryKey: ['associate-list'] });
          } else {
            toast.error(res.data.message || 'Associate Deletion Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
    } finally {
      setIsDeleting(false);
    }
  };

  // handler function to handle change password feature declared here
  const changePasswordHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      const body: AssociatePasswordChangeBody = {
        password: values.new_password,
        is_first_login: false,
        updated_by: currentUser?.username as string,
      };

      await changeAssociatePasswordHandler
        .mutateAsync({
          associateId: (data as Associate).id,
          body,
        })
        .then((res) => {
          console.log('CHANGE PASS RES ASSOCIATE: ', res);

          if (res.is_success) {
            toast.success('Password Updated!!');

            queryClient.invalidateQueries({ queryKey: ['associate-list'] });
            setShowPasswordModal(false);
          } else {
            toast.error(res.message || 'Password Update Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
    } finally {
      actions.setSubmitting(false);
    }
  };

  // handler function to handle associate rejection feature declared here
  const rejectAssociateHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      await updateAssociateStatusHandler
        .mutateAsync({
          associateId: (data as Associate).id,
          body: {
            user: (data as Associate).id,
            reviewed_by: currentUser?.username as string,
            status: values.status,
            comments: values.comments,
            is_resubmit: false,
            updated_by: currentUser?.username as string,
          },
        })
        .then((res) => {
          if (res.is_success) {
            toast.success('Associate Rejected!!');

            setShowRejectModal(false);
            queryClient.invalidateQueries({ queryKey: ['associate-list'] });
          } else {
            toast.error(res.message || 'Rejection Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // handler function to deactivate associate declared here
  const deactivateAssociateHandler = async () => {
    try {
      setIsDeactivating(true);

      await toggleAssociateActivityHandler
        .mutateAsync({
          associateId: (data as Associate).id,
          body: {
            user: {
              is_active: !(data as Associate).is_active,
            },
            user_profile: { languages_spoken: null },
            feature_permissions: null,
          },
        })
        .then((res) => {
          console.log('DEACTIVATE RES: ', res);
          if (res.is_success) {
            toast.success(
              `Associate ${res.data.is_active ? 'Activated' : 'Deactivated'}!!`
            );

            queryClient.invalidateQueries({ queryKey: ['associate-list'] });
            setShowDeactivateModal(false);
          } else {
            toast.error(
              res.message ||
                `${res.data.is_active ? 'Activation' : 'Deactivation'} Failed!!`
            );
          }
        });
    } catch (error) {
    } finally {
      setIsDeactivating(false);
    }
  };

  // rendering user mutation modals section component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showUpdateModal && (
          <CreateEditAssociateModal
            title='update associate'
            associate={data as Associate}
            associateProfile={getUserProfileByIdHandler.data?.data}
            isProfileLoading={getUserProfileByIdHandler.isLoading}
            handleModalClose={() => setShowUpdateModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showUpdatePermissionModal && (
          <UpdatePermissionsModal
            user={{
              id: (data as Associate).id,
              name: showFullName(
                (data as Associate).first_name,
                (data as Associate).middle_name,
                (data as Associate).last_name
              ),
            }}
            userRole={{
              id: (data as Associate).role_id,
              name: (data as Associate).role_name,
            }}
            handleModalClose={() => setShowUpdatePermissionModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showPasswordModal && (
          <ChangePasswordModal
            changePasswordFn={changePasswordHandler}
            handleModalClose={() => setShowPasswordModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showRejectModal && (
          <ApplicationConsentModal
            subTitle={<>Are you sure you want to reject this associate?</>}
            status='rejected'
            mutationHandler={rejectAssociateHandler}
            handleModalClose={() => setShowRejectModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeactivateModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to{' '}
                {(data as Associate).is_active ? 'deactivate' : 'activate'} this
                associate{' '}
                <span className='font-semibold text-base-light-blue'>
                  (
                  {showFullName(
                    (data as Associate).first_name,
                    (data as Associate).middle_name,
                    (data as Associate).last_name
                  )}
                  )
                </span>
                ?
              </>
            }
            isLoading={isDeactivating}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deactivateAssociateHandler}
            handleModalClose={() => setShowDeactivateModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this associate{' '}
                <span className='font-semibold text-base-light-blue'>
                  (
                  {showFullName(
                    (data as Associate).first_name,
                    (data as Associate).middle_name,
                    (data as Associate).last_name
                  )}
                  )
                </span>
                ? Once you delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default AssociateMutationModals;
